import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, ArrowsClockwise, TrendUp, TrendDown,
  CurrencyDollar, ChartLineUp, Clock, WarningDiamond, ArrowRight, Bank
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

function ChangeIndicator({ value, suffix = "%" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-red-400" : "text-emerald-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(2)}{suffix}
    </span>
  );
}

export default function M2Report() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState("5y");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/m2-money-supply`);
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load M2 data");
    } finally {
      setLoading(false);
    }
  };

  const current = data?.current || {};
  const chartData = chartView === "all"
    ? (data?.history || [])
    : chartView === "1y"
    ? (data?.chart_1y || [])
    : (data?.chart_5y || []);

  const milestones = data?.milestones || [];

  return (
    <div className="p-5 max-w-[1400px] space-y-5" data-testid="m2-report">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-teal-900/20 flex items-center justify-center">
            <Bank size={20} weight="bold" className="text-teal-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-heading font-bold text-zinc-50">M2 Money Supply</h2>
              <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-teal-900/30 text-teal-400 h-4">USA</Badge>
            </div>
            <p className="text-xs text-zinc-500">Total U.S. money supply including cash, checking, savings, and money market funds</p>
          </div>
        </div>
        <button
          data-testid="m2-refresh-btn"
          onClick={fetchData}
          disabled={loading}
          className="text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 px-2.5 py-1.5 border border-zinc-800 rounded-sm hover:bg-zinc-900 transition-colors"
        >
          <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 bg-zinc-900/60" />)}</div>
      ) : error ? (
        <div className="bg-red-950/20 border border-red-800/30 rounded-sm p-4">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 flex items-center gap-1"><CurrencyDollar size={10} /> Current M2</p>
              <p className="text-2xl font-data font-bold text-teal-400 mt-1">${current.value_trillions}T</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">${current.value_billions?.toLocaleString()}B</p>
            </div>
            <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 flex items-center gap-1"><Clock size={10} /> As of</p>
              <p className="text-lg font-data font-bold text-zinc-200 mt-1">{current.date}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">Monthly data</p>
            </div>
            <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 flex items-center gap-1"><TrendUp size={10} /> Month/Month</p>
              <div className="mt-1"><ChangeIndicator value={current.mom_change_pct} /></div>
              <p className="text-[10px] text-zinc-500 mt-0.5">vs. prior month</p>
            </div>
            <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 flex items-center gap-1"><TrendUp size={10} /> Year/Year</p>
              <div className="mt-1"><ChangeIndicator value={current.yoy_change_pct} /></div>
              <p className="text-[10px] text-zinc-500 mt-0.5">vs. same month last year</p>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
                <ChartLineUp size={14} weight="bold" className="text-teal-400" /> M2 Money Supply Chart
              </h3>
              <Tabs value={chartView} onValueChange={setChartView}>
                <TabsList className="h-7 bg-zinc-900/80 border border-amber-500/20 p-0.5 gap-0.5">
                  <TabsTrigger value="1y" className="text-[10px] px-2 py-1 h-6" data-testid="m2-chart-1y">1Y</TabsTrigger>
                  <TabsTrigger value="5y" className="text-[10px] px-2 py-1 h-6" data-testid="m2-chart-5y">5Y</TabsTrigger>
                  <TabsTrigger value="all" className="text-[10px] px-2 py-1 h-6" data-testid="m2-chart-all">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {chartData.length > 0 && (
              <CpiChart
                data={chartData.map(d => ({ date: d.date, value: d.value }))}
                dataKey="value"
                color="#14b8a6"
                height={280}
                label="M2 ($B)"
              />
            )}
          </div>

          {/* Milestones Table */}
          {milestones.length > 0 && (
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-zinc-800/50">
                <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
                  <Clock size={14} weight="bold" className="text-teal-400" /> Historical M2 Milestones
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="m2-milestones-table">
                  <thead>
                    <tr className="border-b border-zinc-800/60 bg-zinc-900/60">
                      <th className="text-left px-4 py-2.5 text-zinc-500 font-medium">Year</th>
                      <th className="text-right px-4 py-2.5 text-zinc-500 font-medium">M2 ($B)</th>
                      <th className="text-right px-4 py-2.5 text-zinc-500 font-medium">M2 ($T)</th>
                      <th className="text-right px-4 py-2.5 text-zinc-500 font-medium">Growth from 2000</th>
                    </tr>
                  </thead>
                  <tbody>
                    {milestones.map((m, i) => {
                      const base = milestones[0]?.value || 1;
                      const growth = ((m.value - base) / base * 100);
                      return (
                        <tr key={i} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                          <td className="px-4 py-2 text-zinc-300 font-medium">{m.year}</td>
                          <td className="px-4 py-2 text-zinc-200 text-right font-data">${m.value.toLocaleString()}</td>
                          <td className="px-4 py-2 text-zinc-200 text-right font-data">${(m.value / 1000).toFixed(2)}T</td>
                          <td className={`px-4 py-2 text-right font-data ${growth > 0 ? "text-red-400" : "text-zinc-400"}`}>
                            {i === 0 ? "--" : `+${growth.toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Educational Content */}
          <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm overflow-hidden">
            <div className="p-5 border-b border-zinc-800/50">
              <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
                <CurrencyDollar size={14} weight="bold" className="text-teal-400" /> What is M2 Money Supply?
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-zinc-300 leading-relaxed">
                M2 is a measure of the total amount of money circulating in the U.S. economy. It includes all physical currency (cash and coins), checking account balances, savings accounts, money market accounts, and certificates of deposit under $100,000. When the Federal Reserve "prints money," it shows up as M2 expansion. When the Fed tightens monetary policy, M2 growth slows or contracts.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-4">
                  <h4 className="text-[10px] uppercase tracking-wider text-teal-400 mb-2 font-medium">What's Included in M2</h4>
                  <ul className="space-y-1.5">
                    {[
                      "M1 (cash, coins, checking deposits, traveler's checks)",
                      "Savings deposits (including money market deposit accounts)",
                      "Time deposits under $100,000 (CDs)",
                      "Retail money market mutual fund shares",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ArrowRight size={9} weight="bold" className="text-teal-400 flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] text-zinc-400 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-4">
                  <h4 className="text-[10px] uppercase tracking-wider text-amber-400 mb-2 font-medium">Why M2 Matters for Markets</h4>
                  <ul className="space-y-1.5">
                    {[
                      "M2 expansion = more money chasing assets = inflation pressure + higher asset prices",
                      "M2 contraction = less money in the system = deflationary pressure + lower asset prices",
                      "Bitcoin and crypto often track M2 trends — BTC rallied as M2 expanded 2020-2021, fell as M2 contracted 2022",
                      "Stocks (SPX) tend to rise when M2 expands and struggle when M2 contracts",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ArrowRight size={9} weight="bold" className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] text-zinc-400 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-4">
                <h4 className="text-[10px] uppercase tracking-wider text-violet-400 mb-2 font-medium">Key Historical Context</h4>
                <ul className="space-y-1.5">
                  {[
                    "Jan 2020: M2 was ~$15.4 trillion. By early 2022, it hit $21.7 trillion — a 40% increase in just 2 years due to COVID stimulus, PPP loans, and Fed quantitative easing.",
                    "2022-2023: The Fed began quantitative tightening (QT). M2 contracted for the first time since records began — falling from $21.7T to ~$20.8T. This was historically unprecedented.",
                    "The correlation: In Feb 2020, BTC was $9,000 and SPX was 3,300. By Nov 2021 (peak M2 growth), BTC hit $69,000 and SPX hit 4,800. When M2 contracted, both fell sharply.",
                    "Every major stock market rally in history has been accompanied by M2 expansion. M2 is the 'fuel' — the more money in the system, the more flows into risk assets.",
                    "M2 as % of GDP indicates whether money growth is productive (growing the economy) or inflationary (devaluing the currency).",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <ArrowRight size={9} weight="bold" className="text-violet-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[10px] text-zinc-400 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom line */}
          <div className="bg-teal-900/10 border border-teal-800/30 rounded-sm p-5" data-testid="m2-bottom-line">
            <h3 className="text-xs font-medium text-teal-300 mb-2 flex items-center gap-2">
              <WarningDiamond size={14} weight="bold" /> The Bottom Line
            </h3>
            <p className="text-xs text-zinc-300 leading-relaxed">
              <strong className="text-zinc-100">M2 is the single most important macro indicator for asset prices.</strong> When the money supply expands, asset prices (stocks, crypto, real estate) tend to rise because there's more money chasing the same assets. When M2 contracts, asset prices fall. Watch M2 trends alongside the Fed's balance sheet for the clearest signal of where markets are heading. As the saying goes: "Don't fight the Fed" — and M2 is how you see what the Fed is actually doing, not just what they're saying.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
